import * as React from 'react';

import gql from 'graphql-tag';
import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';
import { compose } from 'underscore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import waitForGraphQL from 'js/lib/waitForGraphQL';
import waitForProps from 'js/lib/waitForProps';

import { TooltipWrapper } from 'bundles/common/components/Tooltip';
import {
  loadSessionsForCourse,
  loadSessionsForUnprivileged,
  selectAll,
  selectOnDemand,
  selectSession,
} from 'bundles/discussions/actions/SessionFilterActions';
import SessionFilterDropdown from 'bundles/discussions/components/personalization-sessionFilter/SessionFilterDropdown';
import Icon from 'bundles/iconfont/Icon';

import _t from 'i18n!nls/discussions';

import 'css!./__styles__/SessionFilter';

class SessionFilter extends React.Component {
  static propTypes = {
    courseId: PropTypes.string,
    loaded: PropTypes.bool,
    currentSessionId: PropTypes.string,
    discussionsLandingPageActive: PropTypes.bool,
    containsMultipleBranches: PropTypes.bool,
    hasModerationRole: PropTypes.bool,
    isPrivateSession: PropTypes.bool,
    forceLoad: PropTypes.bool,
    isSessionsV2Enabled: PropTypes.bool,
  };

  static contextTypes = {
    executeAction: PropTypes.func.isRequired,
  };

  declare context: LegacyContextType<typeof SessionFilter.contextTypes>;

  static defaultProps = {
    forceLoad: false,
  };

  constructor(props: $TSFixMe, context: $TSFixMe) {
    super(props, context);

    if (!props.loaded && props.hasModerationRole) {
      context.executeAction(loadSessionsForCourse, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }

    if (!props.loaded && !props.hasModerationRole) {
      context.executeAction(loadSessionsForUnprivileged, {
        courseId: props.courseId,
        currentSessionId: props.currentSessionId,
        isPrivateSession: props.isPrivateSession,
      });
    }
  }

  handleDropdownChange = (filterId: $TSFixMe, branchId: $TSFixMe) => {
    if (filterId === 'ondemand') {
      this.context.executeAction(selectOnDemand);
    } else if (filterId === 'all') {
      this.context.executeAction(selectAll, { branchId });
    } else {
      this.context.executeAction(selectSession, { sessionId: filterId });
    }
  };

  render() {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'discussionsLandingPageActive' does not e... Remove this comment to see the full error message
    const { discussionsLandingPageActive, loaded, hasModerationRole, isSessionsV2Enabled } = this.props;

    if (!discussionsLandingPageActive || !loaded || !hasModerationRole) {
      return null;
    }

    const tooltipText = _t(
      `Instructors can now change the structure of the discussion forums for each version of the course to better
        fit the material. It is still possible to view all discussions in a particular version, but no longer
        possible to view discussions across every session.`
    );

    const tooltipId = 'session-filter-tooltip';

    /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
    return (
      <div className="rc-SessionFilter card-rich-interaction cozy">
        <div>
          <div className="horizontal-box align-items-spacebetween">
            <div>
              <h2 className="label-text color-secondary-text title">
                <Icon name="lock" />
                &nbsp;
                {isSessionsV2Enabled ? _t('Filter by version and cohort') : _t('Filter by session')}
              </h2>
            </div>
            {/* @ts-expect-error ts-migrate(2339) FIXME: Property 'containsMultipleBranches' does not exist... Remove this comment to see the full error message */}
            {this.props.containsMultipleBranches ? (
              <TooltipWrapper placement="bottom" tooltipId={tooltipId} message={tooltipText}>
                <div className="label-text color-secondary-text title" tabIndex={0} aria-label={tooltipText}>
                  <Icon name="question-circle-o" className="cif-lg" />
                </div>
              </TooltipWrapper>
            ) : null}
          </div>

          <p className="caption-text color-secondary-text align-left">{_t('Visible to staff and mentors')}</p>

          <SessionFilterDropdown
            isSessionsV2Enabled={isSessionsV2Enabled}
            handleDropdownChange={this.handleDropdownChange}
          />
        </div>
      </div>
    );
  }
}

export default compose(
  connectToStores(
    ['SessionFilterStore', 'SessionStore', 'CourseStore', 'CourseMembershipStore', 'ApplicationStore'],
    ({ SessionFilterStore, SessionStore, CourseStore, CourseMembershipStore, ApplicationStore }, props) => {
      return {
        hasModerationRole: CourseMembershipStore.hasModerationRole() || ApplicationStore.isSuperuser(),
        loaded: SessionFilterStore.filtersLoaded,
        containsMultipleBranches: !!SessionFilterStore.containsMultipleBranches,
        currentSessionId: SessionStore.isEnrolled() ? SessionStore.getSessionId() : undefined,
        courseSlug: CourseStore.getCourseSlug(),
        courseId: CourseStore.getCourseId(),
        isPrivateSession: SessionStore.isEnrolled() ? SessionStore.isSessionPrivate() : false,
      };
    }
  ),
  waitForProps(['courseId']),
  waitForGraphQL(
    gql`
      query FilterSessionsV2AuthoringEligibilityCheckQuery($courseId: String!) {
        SessionsV2AuthoringEligibilityCheckV1Resource {
          get(id: $courseId) {
            eligibleForSessionsV2
          }
        }
      }
    `,
    {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'courseId' does not exist on type '{}'.
      options: ({ courseId }) => ({
        variables: {
          courseId,
        },
      }),
      props: ({ data }) => {
        // @ts-expect-error ts-migrate(2339) FIXME: Property 'SessionsV2AuthoringEligibilityCheckV1Res... Remove this comment to see the full error message
        const { SessionsV2AuthoringEligibilityCheckV1Resource } = data;
        if (SessionsV2AuthoringEligibilityCheckV1Resource) {
          const { get } = SessionsV2AuthoringEligibilityCheckV1Resource;
          return {
            isSessionsV2Enabled: get && get.eligibleForSessionsV2,
          };
        }
        return {
          isSessionsV2Enabled: false,
        };
      },
    }
  ),
  connectToRouter((router, props) => ({
    discussionsLandingPageActive:
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'forceLoad' does not exist on type '{}'.
      props.forceLoad ||
      router.isActive(
        {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'courseSlug' does not exist on type '{}'.
          pathname: `/learn/${props.courseSlug}/discussions`,
          query: router.location.query,
        },
        true
      ) ||
      router.isActive(
        {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'courseSlug' does not exist on type '{}'.
          pathname: `/learn/${props.courseSlug}/discussions/all`,
          query: router.location.query,
        },
        true
      ),
  }))
)(SessionFilter);
